/* eslint-disable react/prop-types */
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { css } from '@emotion/react';
import TagManager from 'react-gtm-module';

import SiteContainer from './SiteContainer';

import * as flexAuthAC from './modules/authentication/action/TwilioFlex';
import * as verifyTokenAC from './modules/authentication/action/VerifyToken';
import * as searchParamsAC from './modules/authentication/action/SearchParams';
import * as dashboardActivityAC from './modules/authentication/action/DashboardActivity';

import AuthCheck from './modules/authentication/components/AuthCheck';
import Loadable from 'react-loadable';
import usePrevious from 'app/hooks/usePrevious';
import useExpandedMenu from './common/useExpandedMenu';

import ErrorBoundary from 'app/components/ErrorBoundary';
import Cookies from 'js-cookie';
import {
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';

import { isPageVisible } from 'app/hooks/usePageVisibility';

import * as loginAC from 'app/modules/authentication/action/Login';

// Import the style only once in your app!
import 'react-datasheet-grid/dist/style.css';

import config from 'app/config';

const Authentication = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "authentication" */ './modules/authentication/Routes'
        ),
    loading: () => null,
    modules: ['authentication'],
    webpack: () => [require.resolveWeak('./modules/authentication/Routes')], // Prevents white flash
});

const publicCss = css`
    width: 100%;
    height: 100%;
`;

const tagManagerArgs = {
    gtmId: 'GTM-WVV5DJN',
    dataLayerName: 'PageDataLayer',
    events: {
        crudListSearch: 'CRUD List Search',
        customEvent: 'Custom Event',
    },
};

if (process.env.NODE_ENV === 'production') {
    TagManager.initialize(tagManagerArgs);
}

class App extends React.Component {
    constructor() {
        super();

        this.state = {
            showNav: true,
            passedAuthToken: '',
        };
    }

    lastStateChange = null;
    lastPage = '';
    editPagePath = '';

    isUnauthorized = () => {
        this.props.vtActions.verifyToken(Cookies.get(config.cookieName));
    };

    componentDidMount() {
        if (this.props.location.search.indexOf('twilioFlexAuth=') !== -1) {
            var url = new URLSearchParams(this.props.location.search);
            this.props.flexActions.loginUserWithFlexIdentity(
                url.get('twilioFlexAuth'),
                url.get('twilioFlexToken')
            );
        } else {
            if (this.props.location.search.indexOf('authToken') === -1) {
                this.props.vtActions.verifyToken(
                    Cookies.get(config.cookieName)
                );
            }
        }

        this.props.spActions.decodeSearchParams(
            this.props,
            {
                searchParams: {},
            },
            true,
            true
        );

        this.timer = setInterval(() => {
            const editPagePath =
                this.props.dashboardActivity.editPagePath ===
                this.props.location.pathname
                    ? true
                    : false;

            // Don't update activity if the page isn't active
            if (!isPageVisible()) {
                return;
            }

            if (
                this.editPagePath !== editPagePath ||
                this.lastPage !== this.props.location.pathname ||
                new Date() - this.lastStateChange > 60000 ||
                (editPagePath && new Date() - this.lastStateChange > 9000)
            ) {
                this.lastStateChange = new Date();
                this.lastPage = this.props.location.pathname;
                this.editPagePath = editPagePath;

                if (this.props.auth.isAuthenticated) {
                    this.props.daActions.updateDashboardActivity(
                        this.props.auth,
                        this.props.location.pathname,
                        this.props.location.search,
                        this.props.dashboardActivity.editFormPath ===
                            this.props.location.pathname
                            ? true
                            : false,
                        editPagePath,
                        this.props.dashboardActivity.resourceName,
                        this.props.dashboardActivity.resourceId,
                        this.isUnauthorized
                    );
                }
            }
        }, 1000);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    componentDidUpdate(prevProps) {
        const currentPath = this.props.location.pathname;

        if (
            this.props.location.pathname !== prevProps.location.pathname ||
            this.props.location.search !== prevProps.location.search
        ) {
            this.props.spActions.decodeSearchParams(
                this.props,
                prevProps,
                this.props.location.pathname !== prevProps.location.pathname,
                this.props.location.search !== prevProps.location.search
            );
        }

        if (this.props.searchParams.spCount > prevProps.searchParams.spCount) {
            if (this.props.searchParams.hideNav === 'true') {
                this.setState({
                    showNav: false,
                });
            }
            if (this.props.searchParams.authToken) {
                // this allows you to redirect while prefilling in some filters
                // we remove a few unused search params, and remove the authToken
                // then we append any remaining search params back onto the path
                const ignoreProperties = [
                    'authToken',
                    'spCount',
                    'prevPath',
                    'path',
                    'expandedMenuSections',
                    'sideNavScrollPosition',
                ];
                const retainedSearchParams = {};
                Object.keys(this.props.searchParams)
                    .filter((x) => !ignoreProperties.includes(x))
                    .map((item) => {
                        retainedSearchParams[item] =
                            this.props.searchParams[item];
                    });

                let pathName = this.props.location.pathname;
                if (Object.keys(retainedSearchParams).length > 0) {
                    pathName +=
                        '?' +
                        Object.keys(retainedSearchParams)
                            .map((key) => key + '=' + retainedSearchParams[key])
                            .join('&');
                }

                const scroll =
                    this.props.searchParams.sideNavScrollPosition ?? 0;
                if (this.props.searchParams.expandedMenuSections) {
                    const expandedMenuSections =
                        this.props.searchParams.expandedMenuSections
                            .split(',')
                            .map((i) => parseInt(i));
                    this.props.setExpandedMenuSections({
                        indexes: expandedMenuSections,
                        enabled: true,
                        scroll,
                    });
                } else {
                    this.props.setExpandedMenuSections({
                        ...this.props.expandedMenuSections,
                        enabled: false,
                        scroll,
                    });
                }

                if (scroll) {
                    window.setSideNavScrollTop = scroll;
                }

                this.props.router.navigate(pathName, {
                    replace: true,
                });

                this.props.loginActions.setUserToken(
                    this.props.searchParams.authToken
                );
            }
        }

        if (
            this.props.auth.isAuthenticated &&
            this.props.searchParams.spCount > 0 &&
            this.props.searchParams.nextPath
        ) {
            if (this.props.searchParams.nextPath.startsWith(config.nextURI)) {
                window.location.assign(this.props.searchParams.nextPath);
            } else {
                this.props.router.navigate(this.props.searchParams.nextPath);
            }
        } else if (
            this.props.auth.isAuthenticated &&
            !prevProps.auth.isAuthenticated &&
            currentPath.startsWith('/auth')
        ) {
            this.props.router.navigate('/');
        }

        if (
            this.props.dashboardActivity?.activity?.minDashboardVersion &&
            prevProps.dashboardActivity?.activity?.minDashboardVersion &&
            this.props.dashboardActivity?.activity?.minDashboardVersion !==
                prevProps.dashboardActivity?.activity?.minDashboardVersion
        ) {
            window.location.reload();
        }
    }

    render() {
        const props = this.props;

        if (
            !props.auth.isAuthenticated &&
            !props.auth.didAttemptTokenVerification
        ) {
            return <AuthCheck />;
        }

        if (!props.auth.isAuthenticated) {
            return (
                <div css={publicCss}>
                    <Routes>
                        <Route
                            path="/auth/*"
                            element={
                                <Authentication
                                    navigate={props.router.navigate}
                                />
                            }
                        />
                        <Route
                            path="*"
                            element={
                                <Navigate
                                    to={`/auth/login?nextPath=${props.location.pathname}`}
                                    replace
                                />
                            }
                        />
                    </Routes>
                </div>
            );
        }

        return (
            <div id="app">
                <ErrorBoundary auth={props.auth} location={props.location}>
                    <SiteContainer
                        showNav={this.state.showNav}
                        location={props.location}
                        auth={props.auth}
                        dashboardActivity={props.dashboardActivity}
                    />
                </ErrorBoundary>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    auth: state.auth,
    searchParams: state.searchParams,
    dashboardActivity: state.dashboardActivity,
});

const mapDispatchToProps = (dispatch) => ({
    flexActions: bindActionCreators(flexAuthAC, dispatch),
    vtActions: bindActionCreators(verifyTokenAC, dispatch),
    spActions: bindActionCreators(searchParamsAC, dispatch),
    daActions: bindActionCreators(dashboardActivityAC, dispatch),
    loginActions: bindActionCreators(loginAC, dispatch),
});

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        const location = useLocation();
        const navigate = useNavigate();
        const params = useParams();

        const currentLocation = location.pathname;
        const prevLocation = usePrevious(currentLocation);

        return (
            <Component
                {...props}
                router={{ location, navigate, params }}
                location={location}
                previousLocationPath={prevLocation}
            />
        );
    }

    return ComponentWithRouterProp;
}

export const withExpandedMenu = (Component) => {
    // eslint-disable-next-line react/display-name
    return (props) => {
        const [expandedMenuSections, setExpandedMenuSections] =
            useExpandedMenu();

        return (
            <Component
                expandedMenuSections={expandedMenuSections}
                setExpandedMenuSections={setExpandedMenuSections}
                {...props}
            />
        );
    };
};

export default withRouter(
    withExpandedMenu(connect(mapStateToProps, mapDispatchToProps)(App))
);
